import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register } from '../utils/auth';
import '../styles/Auth.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faLock, 
  faEye, 
  faEyeSlash, 
  faUser, 
  faBuilding,
  faArrowLeft,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'customer',
    fullName: '',
    businessName: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [validations, setValidations] = useState({
    email: true,
    password: true,
    fullName: true,
    businessName: true
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    if (formSubmitted) {
      validateForm();
    }
  }, [formData, formSubmitted]);

  const validateForm = () => {
    const newValidations = {
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
      password: formData.password.length >= 5,
      fullName: formData.role === 'customer' ? formData.fullName.trim().length >= 2 : true,
      businessName: formData.role === 'vendor' ? formData.businessName.trim().length >= 2 : true
    };
    
    setValidations(newValidations);
    return Object.values(newValidations).every(value => value === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    const name = formData.role === 'vendor' ? formData.businessName : formData.fullName;
    
    try {
      const result = await register(formData.email, formData.password, formData.role, name);
      console.log('Registration successful:', result);
      
      // Show success message
      navigate('/login', { 
        state: { 
          message: 'Account created successfully! Please log in.' 
        }
      });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <button 
              className="back-button-auth" 
              onClick={() => navigate('/')}
              aria-label="Back to home"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1>Create Account</h1>
            <p>Join our community today</p>
          </div>

          {error && (
            <div className="error-message">
              <FontAwesomeIcon icon={faExclamationCircle} />
              <span>{error}</span>
            </div>
          )}
          
          <div className="role-selector">
            <button 
              className={`role-option ${formData.role === 'customer' ? 'active' : ''}`}
              onClick={() => setFormData({...formData, role: 'customer'})}
              type="button"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faUser} />
              <span>Customer</span>
            </button>
            <button 
              className={`role-option ${formData.role === 'vendor' ? 'active' : ''}`}
              onClick={() => setFormData({...formData, role: 'vendor'})}
              type="button"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faBuilding} />
              <span>Business</span>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="auth-form">
            <div className={`form-group ${!validations.email ? 'error' : ''}`}>
              <div className="input-icon-wrapper">
                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                <input 
                  type="email" 
                  name="email"
                  placeholder="Email Address" 
                  value={formData.email} 
                  onChange={handleInputChange} 
                  required
                  disabled={loading}
                />
              </div>
              {!validations.email && formSubmitted && (
                <p className="validation-message">Please enter a valid email address</p>
              )}
            </div>
            
            <div className={`form-group ${!validations.password ? 'error' : ''}`}>
              <div className="input-icon-wrapper">
                <FontAwesomeIcon icon={faLock} className="input-icon" />
                <input 
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password" 
                  value={formData.password} 
                  onChange={handleInputChange} 
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {!validations.password && formSubmitted && (
                <p className="validation-message">Password must be at least 5 characters</p>
              )}
            </div>
            
            {formData.role === 'customer' ? (
              <div className={`form-group ${!validations.fullName ? 'error' : ''}`}>
                <div className="input-icon-wrapper">
                  <FontAwesomeIcon icon={faUser} className="input-icon" />
                  <input 
                    type="text" 
                    name="fullName"
                    placeholder="Full Name" 
                    value={formData.fullName} 
                    onChange={handleInputChange} 
                    required
                    disabled={loading}
                  />
                </div>
                {!validations.fullName && formSubmitted && (
                  <p className="validation-message">Please enter your full name</p>
                )}
              </div>
            ) : (
              <div className={`form-group ${!validations.businessName ? 'error' : ''}`}>
                <div className="input-icon-wrapper">
                  <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                  <input 
                    type="text" 
                    name="businessName"
                    placeholder="Business Name" 
                    value={formData.businessName} 
                    onChange={handleInputChange} 
                    required
                    disabled={loading}
                  />
                </div>
                {!validations.businessName && formSubmitted && (
                  <p className="validation-message">Please enter your business name</p>
                )}
              </div>
            )}
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Creating Account...
                </>
              ) : 'Create Account'}
            </button>
          </form>
          
          <div className="auth-footer">
            <p>Already have an account?</p>
            <Link to="/login" className="auth-link">Log In</Link>
          </div>
          
          {formData.role === 'customer' && (
            <>
              <div className="auth-separator">
                <span>or register as</span>
              </div>
              
              <div className="auth-options">
                <Link to="/vendor/register" className="option-button vendor">
                  Business Owner
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Register;